import React from "react";
import { ArrowUpRight } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import useReveal from "../../hooks/useReveal";

const IMAGES = {
  cyprus:
    "https://images.unsplash.com/photo-1746458538086-138b8c932155?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxjeXBydXMlMjBsdXh1cnklMjBjb2FzdGxpbmUlMjBkdXNrfGVufDB8fHx8MTc3NzgxMTkxMXww&ixlib=rb-4.1.0&q=85",
  turkey:
    "https://images.unsplash.com/photo-1626608212979-4545e29f4e1a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBoaWdoJTIwZW5kJTIwcmVhbCUyMGVzdGF0ZSUyMHR1cmtleXxlbnwwfHx8fDE3Nzc4MTE5MTF8MA&ixlib=rb-4.1.0&q=85",
  uae: "https://images.unsplash.com/photo-1768069794857-9306ac167c6e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxkdWJhaSUyMHNreWxpbmUlMjBsdXh1cnklMjBzdW5zZXR8ZW58MHx8fHwxNzc3ODExOTAyfDA&ixlib=rb-4.1.0&q=85",
};

const LocationCard = ({ img, name, tagline, description, tag, index, testid }) => (
  <article
    className={`group reveal relative overflow-hidden border border-white/5 bg-[#0f0f0f] card-hover ${
      index === 0 ? "lg:row-span-2 lg:col-span-1" : ""
    }`}
    style={{ transitionDelay: `${index * 80}ms` }}
    data-testid={testid}
  >
    <div
      className={`relative w-full ${
        index === 0 ? "h-[460px] lg:h-full lg:min-h-[640px]" : "h-[340px]"
      } overflow-hidden`}
    >
      <img
        src={img}
        alt={name}
        className="w-full h-full object-cover img-zoom"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent"></div>

      <div className="absolute top-6 left-6 flex items-center gap-3">
        <span className="font-mono-l text-[10px] text-[#e8d4a8]">
          0{index + 1} /
        </span>
        <span className="font-mono-l text-[10px] text-white/60">{tag}</span>
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-8 md:p-10">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="font-display text-3xl md:text-4xl text-white leading-none">
              {name}
            </h3>
            <p className="mt-2 font-display italic text-[#e8d4a8] text-sm">
              {tagline}
            </p>
          </div>
          <a
            href="#contact"
            className="w-11 h-11 flex items-center justify-center border border-[#e8d4a8]/40 text-[#e8d4a8] hover:bg-[#e8d4a8] hover:text-black transition-all duration-300 shrink-0"
            data-testid={`${testid}-cta`}
            aria-label={`Explore ${name}`}
          >
            <ArrowUpRight size={16} />
          </a>
        </div>
        <p className="text-white/70 text-sm leading-relaxed max-w-md font-light">
          {description}
        </p>
      </div>
    </div>
  </article>
);

const Locations = () => {
  const { t } = useLanguage();
  const ref = useReveal();

  const items = [
    {
      key: "cyprus",
      img: IMAGES.cyprus,
      ...t.locations.cyprus,
    },
    {
      key: "turkey",
      img: IMAGES.turkey,
      ...t.locations.turkey,
    },
    {
      key: "uae",
      img: IMAGES.uae,
      ...t.locations.uae,
    },
  ];

  return (
    <section
      id="locations"
      ref={ref}
      data-testid="locations-section"
      className="relative py-24 md:py-32 bg-black"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-16 gap-8">
          <div className="max-w-2xl">
            <p
              className="reveal font-mono-l text-[11px] text-[#e8d4a8] mb-5 flex items-center gap-3"
              data-testid="locations-eyebrow"
            >
              <span className="w-6 h-px bg-[#e8d4a8]"></span>
              {t.locations.eyebrow}
            </p>
            <h2
              className="reveal reveal-delay-1 font-display text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05]"
              data-testid="locations-title"
            >
              {t.locations.title}
            </h2>
          </div>
          <p className="reveal reveal-delay-2 text-white/60 max-w-md text-sm md:text-base leading-relaxed font-light">
            {t.locations.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {items.map((item, i) => (
            <LocationCard
              key={item.key}
              img={item.img}
              name={item.name}
              tagline={item.tagline}
              description={item.description}
              tag={item.tag}
              index={i}
              testid={`location-card-${item.key}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Locations;
